import { Link } from 'react-router-dom';
import { Landmark, Users } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../auth';
import { TaskTemplatesSettings } from './TaskTemplatesSettings';
import { UserProfileSettings } from './UserProfileSettings';

// Settings page (Slice B §3). A single scrolling page of sections — matches
// the section-based detail-page pattern (CLAUDE.md), not tabs. Slice B ships
// only the Task Templates section; future settings (roles, branding, …)
// drop in as additional <SettingsSection> blocks here.
function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description && (
          <p className="mt-0.5 text-sm text-slate-500">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export function SettingsPage() {
  const me = useCurrentUser();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Workspace configuration. More sections land in future releases."
      />

      {me.role === 'admin' && (
        <SettingsSection
          title="Users"
          description="Everyone with a Kyck login: their job role, access level, and who is turned off."
        >
          <Button asChild variant="outline">
            <Link to="/settings/users">
              <Users className="h-4 w-4" />
              Manage users
            </Link>
          </Button>
        </SettingsSection>
      )}

      {me.role === 'admin' && (
        <SettingsSection
          title="Integrations"
          description="Connect external systems. Xero brings invoiced revenue into Account Analytics."
        >
          <Button asChild variant="outline">
            <Link to="/settings/xero">
              <Landmark className="h-4 w-4" />
              Xero connections
            </Link>
          </Button>
        </SettingsSection>
      )}

      <SettingsSection
        title="KyckConnect"
        description="Speed to Lead, AI SMS, the lead relay and the number pools now live under KyckConnect in the sidebar. Per-club settings are on each account's KyckConnect tab."
      >
        <Button asChild variant="outline">
          <Link to="/kyckconnect">Open KyckConnect</Link>
        </Button>
      </SettingsSection>

      <SettingsSection
        title="My Profile"
        description="Your name and contact details, used in the signature of franchisee-facing preview emails you send."
      >
        <UserProfileSettings />
      </SettingsSection>

      <SettingsSection
        title="Task Templates"
        description="Reusable task lists you can apply to a Plan in one click. Global templates work for any brand; brand templates are scoped to that brand."
      >
        <TaskTemplatesSettings />
      </SettingsSection>
    </div>
  );
}
